/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

/**
 * Defines the client message handler which is invoked by Worker instances when
 * messages are received.
 */
export type ClientMessageHandler = (message: any) => void;

/**
 * Defines the client error message handler which is invoked by Worker instances
 * when messages can not be deserialized
 */
export type ClientMessageErrorHandler = (event: MessageEvent) => void;

/**
 * Defines the client error message handler which is invoked by Worker instances
 * when an underlying Web Worker error occurs.
 */
export type ClientErrorHandler = (event: ErrorEvent) => void;

/**
 * Defines the NotificationsWorker message types specific to the NotificationsWorker
 * API. This type is defined within the type.ts module so as to allow for being used
 * by both the Worker API and the Worker script.
 */
export enum NotificationMessage {
  INITIALIZE = 'INITIALIZE',
  ACCOUNT_CHANGE = 'ACCOUNT_CHANGE'
}

/**
 * Defines the NotificationsWorker context, which is implemented in this module so
 * as to allow for being used by both the Worker API and the Worker script.
 */
export const NotificationsWorkerContext = 'NotificationsWorker';

/**
 * Defines the account payload which is sent to the NotificationsWorker on
 * INITIALIZE and ACCOUNT_CHANGE. Only the selected global group id, and the
 * ids of every account within the selected group are sent, never the account
 * hierarchy itself.
 *
 * ```typescript
 *
 *    import {NotificationsAccount} from '@ion/core/src/workers/types';
 *    ...
 *
 *    const account: NotificationsAccount = {
 *      globalGroupId : 1234,
 *      accountIds    : [1234, 5678]
 *    };
 *
 * ```
 */
export interface NotificationsAccount {
  globalGroupId: number;
  accountIds: number[];
};
